import { Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { WishlistKind } from "@gaole/shared";
import { useAuth } from "../../stores/auth";
import { toast } from "../../stores/toast";
import { useWishlistIds, useWishlistToggle } from "../../hooks/useWishlistIds";

interface WishlistHeartProps {
  kind: WishlistKind;
  productId?: string;
  listingId?: string;
  className?: string;
}

export function WishlistHeart({ kind, productId, listingId, className = "" }: WishlistHeartProps) {
  const navigate = useNavigate();
  const { user, moduleEnabled } = useAuth();
  const { data: ids } = useWishlistIds();
  const toggle = useWishlistToggle();

  if (kind === "SHOP" && !moduleEnabled("ecommerce")) return null;
  if (kind === "MARKETPLACE" && !moduleEnabled("marketplace")) return null;

  const refId = kind === "SHOP" ? productId : listingId;
  if (!refId) return null;

  const saved =
    kind === "SHOP"
      ? Boolean(ids?.productIds.includes(refId))
      : Boolean(ids?.listingIds.includes(refId));

  function onClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate("/login");
      return;
    }
    toggle.mutate(
      kind === "SHOP" ? { kind, productId: refId } : { kind, listingId: refId },
      {
        onSuccess: (res) => toast("success", res.saved ? "Saved to wishlist" : "Removed from wishlist"),
        onError: (err: Error) => toast("error", err.message),
      },
    );
  }

  return (
    <button
      type="button"
      aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
      aria-pressed={saved}
      disabled={toggle.isPending}
      onClick={onClick}
      className={`flex h-9 w-9 items-center justify-center rounded-full bg-black/45 backdrop-blur-sm transition-colors hover:bg-black/60 ${className}`}
    >
      <Heart
        size={18}
        className={saved ? "fill-brand-pink text-brand-pink" : "text-white/90"}
        strokeWidth={saved ? 0 : 2}
      />
    </button>
  );
}
